import { Box, makeStyles, IconButton } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import React, { useRef, useState, useEffect } from 'react';
import { COLOR_GREY, COLOR_WHITE, PROGRESS_BAR_COLOUR } from '../utilities/MaterialWatchman';
import clsx from 'clsx';
import ChatAttachmentModal from './ChatAttachmentModal';
import { ReactComponent as AddIcon } from '../../resources/images/plus.svg';
import { ReactComponent as SendIcon } from '../../resources/images/chevron-right.svg';
import { loadSessionChatMessageDraft, persistSessionChatMessageDraft, removeSessionChatMessageDraft } from '../../utils/ClientSessionHandler';
import { styleBreakDesktop, styleBreakMobile } from '../utilities/layouts/Globals';
import { maxChatWidth } from '../../utils/ChatModule';

const toBase64 = file =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
    });

const useStyles = makeStyles(theme => ({
    formContainer: {
        position: 'fixed',
        bottom: 0,
        zIndex: 2,
        height: `${theme.spacing(10)}px`,
        backgroundColor: theme.palette.background.default,
        padding: `${theme.spacing(2.5)}px ${theme.spacing(3)}px`,
        [styleBreakMobile(theme)]: {
            width: '100%',
        },
        [styleBreakDesktop(theme)]: {
            width: maxChatWidth(theme),
        },
    },
    button: {
        height: `${theme.spacing(5)}px`,
        width: `${theme.spacing(5)}px`,
        borderRadius: '50%',
        color: COLOR_WHITE,
        backgroundColor: theme.palette.primary.main,
        '& svg': {
            height: `${theme.spacing(3.86)}px`,
            width: `${theme.spacing(3.86)}px`,
        },
        '& span': {
            width: 'auto',
        },
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
        '&:disabled': {
            backgroundColor: COLOR_GREY.main,
        },
    },
    sendButton: {
        '& svg': {
            height: `${theme.spacing(3.86)}px`,
            width: `${theme.spacing(3.86)}px`,
        },
    },
    formInputContainer: {
        width: '100%',
        height: `${theme.spacing(5)}px`,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'flex-end',
        '& textarea': {
            resize: 'none',
            fontFamily: 'inherit',
            margin: '0px',
            width: '100%',
            fontSize: `${theme.spacing(2)}px`,
            border: `1px solid ${COLOR_WHITE}`,
            borderRadius: `${theme.spacing(2.5)}px`,
            color: theme.palette.primary.dark,
            padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
            lineHeight: `${theme.spacing(2.5)}px`,
            minHeight: `${theme.spacing(5)}px`,
            maxHeight: `${theme.spacing(17)}px`,
            boxSizing: 'border-box',
            letterSpacing: `${theme.spacing(0.02)}px`,
            overflowY: 'hidden',
            '&:focus': {
                borderColor: theme.palette.primary.main,
            },
            '&:focus-visible': {
                outline: theme.palette.primary.main,
            },
            '&::placeholder': {
                color: COLOR_GREY.main,
            },
        },
    },
    buttonContainer: {
        width: theme.spacing(5.625),
    },
    attachButton: {
        height: `${theme.spacing(5)}px`,
        width: `${theme.spacing(5)}px`,
        backgroundColor: theme.palette.primary.main,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '& svg': {
            height: `${theme.spacing(3.86)}px`,
            width: `${theme.spacing(3.86)}px`,
            color: COLOR_WHITE,
        },
    },
    attachment: {
        display: 'none',
    },
    filePreview: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        color: theme.palette.primary.light,
        '& img': {
            width: theme.spacing(9),
            height: theme.spacing(9),
            borderRadius: theme.spacing(1),
            marginRight: theme.spacing(1),
        },
    },
    removePreview: {
        position: 'absolute',
        padding: theme.spacing(1.25),
        top: -theme.spacing(1.25),
        left: theme.spacing(7.5),
        height: theme.spacing(2),
        width: theme.spacing(2),
        backgroundColor: COLOR_WHITE,
        borderRadius: '50%',
        border: `1px solid ${theme.palette.primary.dark}`,
        '& svg': {
            width: theme.spacing(2),
            height: theme.spacing(2),
        },
    },
    typingText: {
        position: 'absolute',
        top: -theme.spacing(1.5),
        left: 0,
        fontSize: theme.spacing(1.5),
        color: COLOR_GREY.main,
        padding: `${theme.spacing(0.2)}px ${theme.spacing(1.2)}px`,
        backgroundColor: COLOR_WHITE,
    },
}));

export async function uploadToMXC(client, name, fileAttachment) {
    try {
        return await client.uploadContent(fileAttachment, { name, includeFilename: true });
    } catch (e) {
        console.error(e);
        // if disconnected, send anyway so user can delete/resend.
        return null;
    }
}

function ChatForm({ typing, onSendMessage, client }) {
    // load message draft. if sessionStorage returns "null", replace with ''
    const [message, setMessage] = useState(loadSessionChatMessageDraft() || '');
    const [fileAttachment, setFileAttachment] = useState(null);
    const [filePreviewUrl, setFilePreviewUrl] = useState(null);
    const formRef = useRef(null);

    const [open, setOpen] = useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const [fileMessagePreviewData, setFileMessagePreviewData] = useState(null);
    const [isTextBoxActive, setTextBoxActive] = useState(false);
    const [textBoxHasContent, setTextBoxHasContent] = useState(false);
    const [textAreaHeight, setTextAreaHeight] = useState('40px');

    const classes = useStyles();

    useEffect(() => {
        if (message.length > 0) {
            setTextBoxHasContent(true);
        } else {
            setTextBoxHasContent(false);
        }
    }, [message]);

    const onSubmit = async e => {
        e.preventDefault();
        const testForEmptyMessage = message.replace(/\s/g, '');

        if (testForEmptyMessage?.length) {
            onSendMessage &&
                onSendMessage({
                    body: message,
                    msgtype: 'm.text',
                });
            setMessage('');
            removeSessionChatMessageDraft();
        } else if (fileAttachment) {
            const name = fileAttachment?.name ?? `capture.${fileAttachment?.type?.split('/')[1]}`;
            const attchmxc = await uploadToMXC(client, name, fileAttachment);

            onSendMessage &&
                onSendMessage({
                    body: name,
                    // TODO: improvement ticket will be figuring out if m.file or m.image so we can exchange non-image files as well
                    msgtype: 'm.image',
                    info: {
                        // TODO: same as above TODO
                        mimetype: `image/${name.split('.')[1]}`,
                        size: fileAttachment?.size,
                    },
                    url: attchmxc ?? fileMessagePreviewData,
                });
            setMessage('');
            removeSessionChatMessageDraft();
            setFileAttachment(null);
            setFilePreviewUrl(null);
        }
    };

    const handleInputChange = input => {
        const chatMessage = input.target.value;
        setMessage(chatMessage);
        handleTextAreaHeight(input);

        if (!chatMessage) {
            removeSessionChatMessageDraft();
        } else {
            persistSessionChatMessageDraft(chatMessage);
        }
    };

    const handleAddedFile = async input => {
        setFileAttachment(input?.file);
        setFilePreviewUrl(input?.uri);

        try {
            const base64fileData = await toBase64(input?.file);
            setFileMessagePreviewData(base64fileData);
        } catch (e) {
            // TODO: handle if upload to memory fails
            console.error(e);
        }
    };

    const removeFilePreview = () => {
        setFileAttachment(null);
        setFilePreviewUrl(null);
    };

    const handleKeypress = e => {
        // if enter key is pressed, submit the form.
        // if enter+shift are pressed, go to new line.
        if (e.keyCode === 13 && e.shiftKey === false && formRef && formRef.current) {
            e.preventDefault();
            // if newlines are inserted at the very end of message, remove them.
            formRef.current && formRef.current.dispatchEvent(new Event('submit', { cancelable: true }));
        }
    };

    const handleTextAreaFocus = e => {
        e.preventDefault();
        setTextBoxActive(true);
        handleTextAreaHeight(e);
    };

    const handleTextAreaNoFocus = e => {
        e.preventDefault();
        setTextBoxActive(false);
        e.target.style.height = '40px';
        setTextAreaHeight(e.target.style.height);
    };

    const handleTextAreaHeight = e => {
        // Reset field height
        e.target.style.height = 'inherit';

        // Calculate the height
        e.target.style.height = `${Math.min(e.target.scrollHeight, 136)}px`;
        setTextAreaHeight(e.target.style.height);
    };

    const fileRef = useRef(null);

    const onBrowseFile = () => {
        fileRef.current.click();
    };

    const onAdd = evt => {
        if (typeof evt === 'string') {
            const file = dataURItoBlob(evt);
            handleAddedFile &&
                handleAddedFile({
                    file,
                    uri: evt,
                });
        } else {
            const f = evt?.target?.files;
            if (f && f.length) {
                const file = f[0];
                const uri = URL.createObjectURL(file);
                handleAddedFile &&
                    handleAddedFile({
                        file,
                        uri,
                    });
            }
        }
        handleClose && handleClose();
    };

    const isMobile = () => {
        const isMobile = window?.navigator?.userAgentData?.mobile;
        const ua = window?.navigator?.userAgent;
        const iOS = !!ua?.match(/iP(ad|hone)/i);
        const webkit = !!ua?.match(/WebKit/i);
        const iOSSafari = iOS && webkit && !ua?.match(/CriOS/i);

        return isMobile || iOSSafari;
    };

    return (
        <form
            ref={formRef}
            onSubmit={onSubmit}
            className={clsx(classes.formContainer, { [classes.withPreview]: filePreviewUrl })}
            style={{ backgroundColor: isTextBoxActive || textBoxHasContent ? COLOR_WHITE : '' }}
        >
            {typing && <div className={classes.typingText}>{typing}</div>}
            <Box display="flex" alignItems="center" flexDirection="row" justifyContent="center">
                {!isTextBoxActive && !textBoxHasContent && (
                    <Box className={classes.buttonContainer}>
                        <label htmlFor="icon-button-file">
                            <div className={classes.attachButton}>
                                <AddIcon onClick={isMobile() ? onBrowseFile : handleOpen} />
                                <input ref={fileRef} onChange={onAdd} type="file" style={{ display: 'none' }} />
                                <input id="file-attach" type="file" className={classes.attachment} name="file" onChange={handleAddedFile} />
                            </div>
                        </label>
                    </Box>
                )}
                <Box className={classes.formInputContainer} style={isTextBoxActive || textBoxHasContent ? { paddingRight: '16px' } : { paddingLeft: '16px' }}>
                    {filePreviewUrl ? (
                        <div className={classes.filePreview}>
                            <img alt="file preview url" src={filePreviewUrl} />
                            <IconButton onClick={removeFilePreview} type="button" className={classes.removePreview} aria-label="remove image preview">
                                <Close />
                            </IconButton>
                        </div>
                    ) : (
                        <textarea
                            style={{ borderColor: textBoxHasContent ? PROGRESS_BAR_COLOUR : '', overflowY: textAreaHeight === '136px' ? 'scroll' : '' }}
                            placeholder="Write a message..."
                            value={message}
                            onChange={handleInputChange}
                            onKeyUp={handleKeypress}
                            onFocus={handleTextAreaFocus}
                            onBlur={handleTextAreaNoFocus}
                            rows={1}
                        ></textarea>
                    )}
                </Box>
                {(isTextBoxActive || textBoxHasContent || fileAttachment) && (
                    <Box className={classes.buttonContainer}>
                        <label htmlFor="icon-button-send">
                            <IconButton
                                disabled={!message?.length && !fileAttachment}
                                type="submit"
                                className={clsx(classes.button, classes.sendButton)}
                                aria-label="send message"
                            >
                                <SendIcon />
                            </IconButton>
                        </label>
                    </Box>
                )}
            </Box>
            <ChatAttachmentModal fileRef={fileRef} open={open} onClose={handleClose} onAddedPhoto={onAdd} onCancel={handleClose} onBrowseFile={onBrowseFile} />
        </form>
    );
}

export default ChatForm;

// solution for converting captured camera image into a file
// https://stackoverflow.com/a/7261048/16782019
export function dataURItoBlob(dataURI) {
    // convert base64 to raw binary data held in a string
    // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
    const byteString = atob(dataURI.split(',')[1]);

    // separate out the mime component
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

    // write the bytes of the string to an ArrayBuffer
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
    }

    return new Blob([ab], { type: mimeString });
}